import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ChevronRight, Minus, Plus } from 'lucide-react';
import type { BatchDetailDto, BatchDto, FulfilmentDetailDto, FulfilmentDto, WarehouseDto } from '@thebb/shared';
import { get, post, ApiError } from '../../api';
import { useAuth } from '../../auth';
import { LogoMark } from '../../components/Logo';
import { Camera } from '../../scanner/Camera';
import { beepError, beepSuccess, primeAudio } from '../../scanner/feedback';

/**
 * The handheld app (QR2's scanner shell, ported): a phone layout under /m with the camera on every
 * screen. Pick mode now; receive and count follow, and gift hand-out joins in phase 2.
 */

export function useWarehouse() {
  const read = () => { try { return localStorage.getItem('station.warehouseId') ?? ''; } catch { return ''; } };
  const [warehouseId, setState] = useState(read);
  const set = (v: string) => { setState(v); try { localStorage.setItem('station.warehouseId', v); } catch { /* ignore */ } };
  return { warehouseId, setWarehouseId: set };
}

export const norm = (s: string) => s.trim().toUpperCase();
export const errText = (e: unknown) => (e instanceof ApiError ? e.body.message : e instanceof Error ? e.message : String(e));

export function MobileShell() {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas" onPointerDown={primeAudio}>
      <Outlet />
    </div>
  );
}

export function TopBar({ title, back, right }: { title: string; back?: boolean; right?: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 bg-primary px-3 text-white" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {back ? (
        <button className="rounded-lg p-2 hover:bg-white/10" onClick={() => void navigate({ to: '/m' })} aria-label="Tilbage"><ArrowLeft size={20} /></button>
      ) : (
        <span className="rounded-lg bg-white p-0.5"><LogoMark size={28} /></span>
      )}
      <div className="min-w-0 flex-1 truncate text-base font-semibold">{title}</div>
      {right}
    </header>
  );
}

export function Toast({ msg, tone }: { msg: string | null; tone: 'ok' | 'err' }) {
  if (!msg) return null;
  return <div className={`mx-3 mt-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${tone === 'ok' ? 'bg-success-100 text-success' : 'bg-danger-100 text-danger'}`}>{msg}</div>;
}

/** Home: choose the warehouse, scan a pick note or batch barcode, or tap a task. */
export function MobileHome() {
  const { me, can } = useAuth();
  const navigate = useNavigate();
  // Hand-out staff at a customer's office only have the hand-out scanner.
  useEffect(() => {
    if (me && can('handout.scan') && !can('floor.pick')) void navigate({ to: '/m/udlevering', replace: true });
  }, [me]);
  const { warehouseId, setWarehouseId } = useWarehouse();
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  useEffect(() => {
    if (!warehouseId && warehouses.data?.length) setWarehouseId(warehouses.data.find((w) => w.type === 'operated')?.id ?? warehouses.data[0]!.id);
  }, [warehouses.data, warehouseId]);
  const fulfilments = useQuery({ queryKey: ['m-fulfilments', warehouseId], queryFn: () => get<{ items: FulfilmentDto[] }>(`/fulfilments?stage=pick&perPage=100&warehouseId=${warehouseId}`), enabled: !!warehouseId, refetchInterval: 15_000 });
  const batches = useQuery({ queryKey: ['m-batches', warehouseId], queryFn: () => get<{ items: BatchDto[] }>(`/batches?perPage=100&warehouseId=${warehouseId}`), enabled: !!warehouseId, refetchInterval: 15_000 });
  const singles = (fulfilments.data?.items ?? []).filter((f) => !f.batchId);
  const openBatches = (batches.data?.items ?? []).filter((b) => ['created', 'printed', 'picking'].includes(b.status));

  const onDetect = async (raw: string) => {
    const code = norm(raw);
    if (/^(PO|TR)-\d+$/.test(code)) {
      const r = await get<{ kind: string | null; id: string | null }>(`/inbound/scan?code=${encodeURIComponent(code)}`);
      if (r.id) { beepSuccess(); void navigate({ to: '/m/receive/$kind/$id', params: { kind: r.kind === 'purchase_order' ? 'po' : 'tr', id: r.id } }); return; }
    }
    if (/^B-\d+$/.test(code)) {
      const b = openBatches.find((x) => x.number === code) ?? (await get<{ items: BatchDto[] }>(`/batches?perPage=200`)).items.find((x) => x.number === code);
      if (b) { beepSuccess(); void navigate({ to: '/m/batch/$id', params: { id: b.id } }); return; }
    } else {
      const f = singles.find((x) => x.number === code || String(x.orderNumber) === code) ?? (await get<{ items: FulfilmentDto[] }>(`/fulfilments?q=${encodeURIComponent(code.split('-')[0]!)}&perPage=20`)).items.find((x) => x.number === code || String(x.orderNumber) === code);
      if (f && !f.batchId) { beepSuccess(); void navigate({ to: '/m/pick/$id', params: { id: f.id } }); return; }
      if (f?.batchId) { beepSuccess(); void navigate({ to: '/m/batch/$id', params: { id: f.batchId } }); return; }
    }
    beepError();
    setMsg({ text: `Intet at plukke for ${raw}`, tone: 'err' });
  };

  return (
    <>
      <TopBar
        title={`Pluk · ${me?.name?.split(' ')[0] ?? ''}`}
        right={
          <select className="rounded-lg bg-white/15 px-2 py-1.5 text-sm font-semibold text-white" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            {warehouses.data?.filter((w) => w.active && w.type === 'operated').map((w) => <option key={w.id} value={w.id} className="text-ink">{w.code}</option>)}
          </select>
        }
      />
      <Camera active compact hint="Scan plukseddel, batch eller indkøbsordre" onDetect={(c) => void onDetect(c)} />
      {msg && <Toast msg={msg.text} tone={msg.tone} />}
      <div className="space-y-4 p-3">
        {openBatches.length > 0 && (
          <section>
            <h2 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted">Batches</h2>
            <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
              {openBatches.map((b) => (
                <li key={b.id}>
                  <Link to="/m/batch/$id" params={{ id: b.id }} className="flex items-center gap-3 px-4 py-3 active:bg-primary-50">
                    <div className="min-w-0 flex-1"><div className="font-semibold">{b.number} · {b.shopName}</div><div className="text-xs text-muted">{b.orderCount} ordrer · {b.unitCount} stk · {b.status === 'picking' ? 'i gang' : 'klar'}</div></div>
                    <ChevronRight size={18} className="text-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section>
          <h2 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted">Ordrer til pluk</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
            {singles.map((f) => (
              <li key={f.id}>
                <Link to="/m/pick/$id" params={{ id: f.id }} className="flex items-center gap-3 px-4 py-3 active:bg-primary-50">
                  <div className="min-w-0 flex-1"><div className="font-semibold">{f.number} <span className="font-normal text-muted">· {f.shopName}</span></div><div className="truncate text-xs text-muted">{f.customerName ?? ''} · {f.lineCount} linjer · {f.unitCount} stk{f.status === 'picking' ? ` · i gang (${f.pickedBy ?? ''})` : ''}</div></div>
                  <ChevronRight size={18} className="text-muted" />
                </Link>
              </li>
            ))}
            {singles.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted">{fulfilments.isLoading ? 'Henter…' : 'Intet at plukke lige nu.'}</li>}
          </ul>
        </section>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/m/receive" className="rounded-2xl bg-white px-4 py-4 text-center font-semibold shadow-card active:bg-primary-50">Modtag varer</Link>
          <Link to="/m/count" className="rounded-2xl bg-white px-4 py-4 text-center font-semibold shadow-card active:bg-primary-50">Optælling</Link>
          {can('handout.scan') && <Link to="/m/udlevering" className="rounded-2xl bg-white px-4 py-4 text-center font-semibold shadow-card active:bg-primary-50">Udlevering</Link>}
        </div>
        <div className="pt-2 text-center text-xs text-muted"><Link to="/" className="underline">Til back office</Link></div>
      </div>
    </>
  );
}

type PickTarget = {
  key: string;
  sku: string;
  title: string;
  variantTitle: string | null;
  imageUrl: string | null;
  quantity: number;
  barcodes: string[];
  locations: { id: string | null; code: string; barcode: string; onHand: number }[];
};

/**
 * One line at a time: go to the location and scan it (or confirm by hand), then scan each item
 * or count with + and −, and confirm. A short pick asks for a reason.
 */
function PickStep({ target, index, total, onDone, busy }: { target: PickTarget; index: number; total: number; onDone: (r: { quantity: number; locationId: string | null; shortReason: string | null }) => void; busy: boolean }) {
  const [location, setLocation] = useState<PickTarget['locations'][number] | null>(null);
  const [count, setCount] = useState(0);
  const [reason, setReason] = useState('not_found');
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const suggested = target.locations[0] ?? null;
  useEffect(() => { setLocation(null); setCount(0); setMsg(null); }, [target.key]);
  const scanned = (raw: string) => {
    const code = norm(raw);
    const loc = target.locations.find((l) => norm(l.barcode) === code || norm(l.code) === code);
    if (loc) {
      beepSuccess();
      setLocation(loc);
      setMsg({ text: `Lokation ${loc.code}`, tone: 'ok' });
      return;
    }
    if (target.barcodes.some((b) => norm(b) === code)) {
      if (!location) {
        beepError();
        setMsg({ text: 'Scan lokationen først', tone: 'err' });
        return;
      }
      if (count >= target.quantity) {
        beepError();
        setMsg({ text: `Der skal kun bruges ${target.quantity}`, tone: 'err' });
        return;
      }
      beepSuccess();
      setCount((c) => c + 1);
      setMsg(null);
      return;
    }
    beepError();
    setMsg({ text: `${raw} hører ikke til denne linje`, tone: 'err' });
  };
  const short = location !== null && count < target.quantity;
  return (
    <>
      <Camera active={!busy} compact hint={location ? `Scan varen (${count}/${target.quantity})` : 'Scan lokationen'} onDetect={scanned} />
      {msg && <Toast msg={msg.text} tone={msg.tone} />}
      <div className="space-y-3 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Linje {index + 1} af {total}</div>
        <div className={`rounded-2xl p-4 shadow-card ${location ? 'bg-success-100' : 'bg-white'}`}>
          <div className="text-xs font-medium uppercase tracking-wide text-muted">{location ? 'Lokation bekræftet' : 'Gå til lokation'}</div>
          <div className="text-5xl font-bold tracking-tight text-ink">{(location ?? suggested)?.code ?? '–'}</div>
          {!location && target.locations.length > 1 && <div className="mt-1 text-xs text-muted">Også på: {target.locations.slice(1, 5).map((l) => `${l.code} (${l.onHand})`).join(', ')}</div>}
          {!location && suggested && <button className="mt-2 text-sm font-semibold text-primary underline" onClick={() => { setLocation(suggested); setMsg(null); }}>Kan ikke scanne lokationen</button>}
          {!suggested && <div className="mt-1 text-sm font-semibold text-danger">Ingen beholdning i dette lager</div>}
        </div>
        <div className="flex gap-3 rounded-2xl bg-white p-4 shadow-card">
          {target.imageUrl && <img src={target.imageUrl.startsWith('//') ? `https:${target.imageUrl}` : target.imageUrl} alt="" className="h-16 w-16 rounded-lg object-cover" />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-tight">{target.title}{target.variantTitle && <span className="text-muted"> · {target.variantTitle}</span>}</div>
            <div className="text-xs text-muted">{target.sku}{target.barcodes.length > 1 ? ` · EAN ${target.barcodes[1]}` : ''}</div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-card">
          <button className="flex h-14 w-14 items-center justify-center rounded-xl bg-canvas text-ink active:bg-line disabled:opacity-40" disabled={!location || count <= 0} onClick={() => setCount((c) => c - 1)} aria-label="Færre"><Minus size={24} /></button>
          <div className="text-center"><div className="text-4xl font-bold tabular-nums">{count}<span className="text-xl text-muted"> / {target.quantity}</span></div><div className="text-xs text-muted">plukket</div></div>
          <button className="flex h-14 w-14 items-center justify-center rounded-xl bg-canvas text-ink active:bg-line disabled:opacity-40" disabled={!location || count >= target.quantity} onClick={() => setCount((c) => c + 1)} aria-label="Flere"><Plus size={24} /></button>
        </div>
        {location && count < target.quantity && <button className="w-full text-center text-sm font-semibold text-primary underline" onClick={() => setCount(target.quantity)}>Alle {target.quantity} er taget</button>}
        {short && (
          <select className="input h-12 text-base" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="not_found">Mangel: ikke fundet</option>
            <option value="damaged">Mangel: beskadiget</option>
            <option value="wrong_count">Mangel: forkert antal på lokationen</option>
            <option value="other">Mangel: andet</option>
          </select>
        )}
        <button
          className={`h-14 w-full rounded-2xl text-lg font-semibold text-white shadow-card disabled:opacity-40 ${short ? 'bg-danger' : 'bg-primary'}`}
          disabled={!location || busy}
          onClick={() => onDone({ quantity: count, locationId: location?.id ?? null, shortReason: short ? reason : null })}
        >
          {busy ? 'Gemmer…' : short ? `Bekræft ${count} af ${target.quantity} (mangel)` : `Bekræft ${count} stk`}
        </button>
      </div>
    </>
  );
}

export function Finished({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
      <CheckCircle2 size={64} className="text-success" />
      <div className="text-xl font-semibold">{title}</div>
      {children}
    </div>
  );
}

/** A single order's fulfilment, line by line against the API. */
export function MobilePick() {
  const { id } = useParams({ from: '/m/pick/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ['fulfilment', id], queryFn: () => get<FulfilmentDetailDto>(`/fulfilments/${id}`) });
  const f = detail.data;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['fulfilment', id] }); void qc.invalidateQueries({ queryKey: ['m-fulfilments'] }); };
  const start = useMutation({ mutationFn: () => post(`/fulfilments/${id}/start-picking`), onSuccess: refresh, onError: (e) => setError(errText(e)) });
  useEffect(() => { if (f?.status === 'released' && !start.isPending) start.mutate(); }, [f?.status]);
  const pick = useMutation({
    mutationFn: (v: { lineId: string; quantity: number; locationId: string | null; shortReason: string | null }) => post(`/fulfilments/${id}/lines/${v.lineId}/pick`, { quantityPicked: v.quantity, locationId: v.quantity > 0 ? v.locationId : null, shortReason: v.shortReason }),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => { beepError(); setError(errText(e)); },
  });
  const finish = useMutation({ mutationFn: () => post(`/fulfilments/${id}/finish-pick`), onSuccess: () => { beepSuccess(); refresh(); }, onError: (e) => setError(errText(e)) });
  if (!f) return <><TopBar title="Henter…" back /></>;
  const lines = f.lines;
  const nextIndex = lines.findIndex((l) => l.quantityPicked === 0 && !l.shortReason);
  const current = nextIndex >= 0 ? lines[nextIndex]! : null;
  return (
    <>
      <TopBar title={`${f.number} · ${f.shopName}`} back />
      {error && <Toast msg={error} tone="err" />}
      {f.status === 'picked' || f.status === 'packed' || f.status === 'shipped' ? (
        <Finished title={`${f.number} er plukket`}>
          <div className="text-sm text-muted">Læg varerne til pakkestationen.</div>
          <button className="h-12 rounded-2xl bg-primary px-6 font-semibold text-white" onClick={() => void navigate({ to: '/m' })}>Næste pluk</button>
        </Finished>
      ) : current ? (
        <PickStep
          target={{ key: current.id, sku: current.sku, title: current.title, variantTitle: current.variantTitle, imageUrl: current.imageUrl, quantity: current.quantity, barcodes: current.barcodes, locations: current.pickFrom.map((p) => ({ id: p.locationId, code: p.locationCode, barcode: p.locationBarcode, onHand: p.onHand })) }}
          index={nextIndex}
          total={lines.length}
          busy={pick.isPending || f.status !== 'picking'}
          onDone={(r) => pick.mutate({ lineId: current.id, ...r })}
        />
      ) : (
        <Finished title="Alle linjer er taget">
          <ul className="w-full max-w-sm divide-y divide-line rounded-2xl bg-white text-left text-sm shadow-card">
            {lines.map((l) => <li key={l.id} className="flex justify-between px-4 py-2"><span>{l.sku}</span><span className={l.quantityPicked < l.quantity ? 'text-danger' : ''}>{l.quantityPicked}/{l.quantity} · {l.locationCode ?? ''}</span></li>)}
          </ul>
          <button className="h-14 w-full max-w-sm rounded-2xl bg-primary text-lg font-semibold text-white" disabled={finish.isPending} onClick={() => finish.mutate()}>Afslut pluk</button>
        </Finished>
      )}
    </>
  );
}

/** A gift-shop batch from its pick list: every SKU in walking order, progress kept on the phone. */
export function MobileBatch() {
  const { id } = useParams({ from: '/m/batch/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const storeKey = `m.batch.${id}`;
  const [done, setDone] = useState<Record<string, number>>(() => { try { return JSON.parse(localStorage.getItem(storeKey) ?? '{}') as Record<string, number>; } catch { return {}; } });
  const save = (d: Record<string, number>) => { setDone(d); try { localStorage.setItem(storeKey, JSON.stringify(d)); } catch { /* ignore */ } };
  const detail = useQuery({ queryKey: ['batch', id], queryFn: () => get<BatchDetailDto>(`/batches/${id}`) });
  const b = detail.data;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['batch', id] }); void qc.invalidateQueries({ queryKey: ['m-batches'] }); };
  const start = useMutation({ mutationFn: () => post(`/batches/${id}/start-picking`), onSuccess: refresh, onError: (e) => setError(errText(e)) });
  useEffect(() => { if (b && ['created', 'printed'].includes(b.status) && !start.isPending) start.mutate(); }, [b?.status]);
  const finish = useMutation({ mutationFn: () => post(`/batches/${id}/finish-picking`), onSuccess: () => { beepSuccess(); try { localStorage.removeItem(storeKey); } catch { /* ignore */ } refresh(); }, onError: (e) => { beepError(); setError(errText(e)); } });
  const list = useMemo(() => b?.pickList ?? [], [b]);
  if (!b) return <><TopBar title="Henter…" back /></>;
  const nextIndex = list.findIndex((p) => done[p.sku] === undefined);
  const current = nextIndex >= 0 ? list[nextIndex]! : null;
  const shorts = list.filter((p) => (done[p.sku] ?? p.quantity) < p.quantity);
  return (
    <>
      <TopBar title={`${b.number} · ${b.shopName}`} back />
      {error && <Toast msg={error} tone="err" />}
      {!['created', 'printed', 'picking'].includes(b.status) ? (
        <Finished title={`${b.number} er plukket`}>
          <div className="text-sm text-muted">{b.orderCount} ordrer · {b.unitCount} stk til pakning.</div>
          <button className="h-12 rounded-2xl bg-primary px-6 font-semibold text-white" onClick={() => void navigate({ to: '/m' })}>Næste pluk</button>
        </Finished>
      ) : current ? (
        <PickStep
          target={{ key: current.sku, sku: current.sku, title: current.title, variantTitle: current.variantTitle, imageUrl: current.imageUrl, quantity: current.quantity, barcodes: current.barcodes, locations: current.locationCode ? [{ id: null, code: current.locationCode, barcode: current.locationBarcode ?? current.locationCode, onHand: current.onHand }] : [] }}
          index={nextIndex}
          total={list.length}
          busy={b.status !== 'picking'}
          onDone={(r) => save({ ...done, [current.sku]: r.quantity })}
        />
      ) : (
        <Finished title="Plukliste gennemgået">
          {shorts.length > 0 ? (
            <div className="w-full max-w-sm rounded-2xl bg-danger-100 p-3 text-left text-sm text-danger">
              Mangler på {shorts.map((s) => `${s.sku} (${done[s.sku]}/${s.quantity})`).join(', ')}. Batchen kan først afsluttes, når alt er taget; ret lageret eller annullér og opret batchen igen uden de ordrer.
              <button className="mt-2 block font-semibold underline" onClick={() => save(Object.fromEntries(Object.entries(done).filter(([k]) => !shorts.some((s) => s.sku === k))))}>Gå tilbage til manglerne</button>
            </div>
          ) : (
            <button className="h-14 w-full max-w-sm rounded-2xl bg-primary text-lg font-semibold text-white" disabled={finish.isPending} onClick={() => finish.mutate()}>Afslut pluk af {b.number}</button>
          )}
        </Finished>
      )}
    </>
  );
}
